import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { collection, getDocs, orderBy, query, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { BusinessType } from '../types';

const BusinessTypes: React.FC = () => {
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingType, setEditingType] = useState<BusinessType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BusinessType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    icon: '',
    description: '',
    sortOrder: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  const fetchBusinessTypes = async () => {
    try {
      const q = query(collection(db, 'businessTypes'), orderBy('sortOrder', 'asc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BusinessType[];
      setBusinessTypes(data);
    } catch (error) {
      console.error('業種マスターの取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (businessType?: BusinessType) => {
    if (businessType) {
      setEditingType(businessType);
      setFormData({
        code: businessType.code,
        name: businessType.name,
        icon: businessType.icon,
        description: businessType.description,
        sortOrder: businessType.sortOrder,
        isActive: businessType.isActive,
      });
    } else {
      setEditingType(null);
      setFormData({
        code: '',
        name: '',
        icon: '',
        description: '',
        sortOrder: businessTypes.length + 1,
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingType(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.code || !formData.name) {
        setError('コードと名前は必須です');
        return;
      }

      if (editingType) {
        // 更新
        await updateDoc(doc(db, 'businessTypes', editingType.id), {
          ...formData,
          updatedAt: Timestamp.now(),
        });
        setSuccess('業種を更新しました');
      } else {
        // 新規作成
        await addDoc(collection(db, 'businessTypes'), {
          ...formData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        setSuccess('業種を追加しました');
      }

      handleCloseDialog();
      fetchBusinessTypes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('業種の保存エラー:', error);
      setError('業種の保存に失敗しました');
    }
  };

  const handleOpenDeleteDialog = (businessType: BusinessType) => {
    setDeleteTarget(businessType);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'businessTypes', deleteTarget.id));
      setSuccess('業種を削除しました');
      handleCloseDeleteDialog();
      fetchBusinessTypes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('業種の削除エラー:', error);
      setError('業種の削除に失敗しました');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          業種マスター管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          業種を追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>コード</TableCell>
              <TableCell>アイコン</TableCell>
              <TableCell>業種名</TableCell>
              <TableCell>説明</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  読み込み中...
                </TableCell>
              </TableRow>
            ) : businessTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  業種マスターがありません
                </TableCell>
              </TableRow>
            ) : (
              businessTypes.map((businessType) => (
                <TableRow key={businessType.id}>
                  <TableCell>{businessType.code}</TableCell>
                  <TableCell>
                    <Typography variant="h5">{businessType.icon}</Typography>
                  </TableCell>
                  <TableCell>{businessType.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                      {businessType.description}
                    </Typography>
                  </TableCell>
                  <TableCell>{businessType.sortOrder}</TableCell>
                  <TableCell>
                    <Chip
                      label={businessType.isActive ? '有効' : '無効'}
                      color={businessType.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(businessType)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(businessType)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 追加・編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingType ? '業種を編集' : '業種を追加'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="コード"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="業種名"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="アイコン（絵文字）"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="💈"
              fullWidth
            />

            <TextField
              label="説明"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <TextField
              label="表示順"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: Number(e.target.value) })}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="有効"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingType ? '更新' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>業種を削除</DialogTitle>
        <DialogContent>
          <Typography>
            業種「{deleteTarget?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            ※ この業種を使用している店舗がある場合、注意が必要です。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BusinessTypes;
